<template>
    <Teleport to="body">
        <Transition
            enter-active-class="crop-modal-fade-enter-active"
            leave-active-class="crop-modal-fade-leave-active"
            enter-from-class="crop-modal-fade-enter-from"
            leave-to-class="crop-modal-fade-leave-to"
        >
            <div
                v-if="open"
                class="fixed inset-0 z-[700] bg-black/42 backdrop-blur-[3px] dark:bg-black/56"
                @click="handleBackdropClick"
            />
        </Transition>

        <!-- Desktop centered modal -->
        <Transition
            enter-active-class="crop-modal-panel-enter-active"
            leave-active-class="crop-modal-panel-leave-active"
            enter-from-class="crop-modal-panel-enter-from"
            leave-to-class="crop-modal-panel-leave-to"
        >
            <div
                v-if="open && !isMobileLayout"
                class="fixed start-1/2 top-1/2 z-[710] w-[min(48rem,92vw)] -translate-x-1/2 -translate-y-1/2 rounded-3xl border border-zinc-200 bg-white p-5 shadow-2xl dark:border-zinc-700 dark:bg-zinc-900 rtl:translate-x-1/2"
                @click.stop
            >
                <form
                    novalidate
                    autocomplete="off"
                    @submit.prevent.stop="handleConfirm"
                    @keydown.enter.prevent.stop="handleConfirm"
                >
                <div class="mb-4 flex items-start justify-between gap-3">
                    <div>
                        <h3 class="text-base font-semibold text-zinc-900 dark:text-white">
                            {{ __('Crop image') }}
                        </h3>
                        <p class="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                            {{ __('Adjust the frame, then add the cropped image.') }}
                        </p>
                    </div>

                    <button
                        type="button"
                        class="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-zinc-500 transition hover:bg-zinc-100 dark:hover:bg-zinc-800"
                        :disabled="isProcessing"
                        @click.prevent.stop="handleClose"
                    >
                        <span class="text-lg leading-none">&times;</span>
                    </button>
                </div>

                <div class="crop-modal-stage max-h-[min(50vh,28rem)] min-h-[16rem] overflow-hidden rounded-2xl bg-zinc-100 dark:bg-zinc-950">
                    <img
                        v-if="cropSource"
                        ref="cropImage"
                        :src="cropSource"
                        alt=""
                        class="block max-h-[min(50vh,28rem)] max-w-full"
                    >
                </div>

                <div class="mt-5 flex w-full items-center">
                    <div class="ms-auto flex items-center gap-2">
                        <button
                            type="button"
                            class="crop-modal-confirm-btn inline-flex h-10 shrink-0 items-center justify-center rounded-full px-5 text-sm font-semibold text-white shadow-[0_8px_22px_rgba(59,130,246,0.32)] transition active:scale-[0.99] disabled:opacity-60"
                            :disabled="isProcessing"
                            @click.prevent.stop="handleConfirm"
                        >
                            {{ confirmLabel }}
                        </button>
                        <button
                            type="button"
                            class="inline-flex h-10 items-center rounded-2xl px-4 text-sm font-medium text-zinc-600 transition hover:bg-zinc-100 disabled:opacity-60 dark:text-zinc-300 dark:hover:bg-zinc-800"
                            :disabled="isProcessing"
                            @click.prevent.stop="handleClose"
                        >
                            {{ __('Cancel') }}
                        </button>
                    </div>
                </div>
                </form>
            </div>
        </Transition>

        <!-- Mobile bottom sheet -->
        <Transition
            enter-active-class="crop-sheet-enter-active"
            leave-active-class="crop-sheet-leave-active"
            enter-from-class="crop-sheet-enter-from"
            leave-to-class="crop-sheet-leave-to"
        >
            <div
                v-if="open && isMobileLayout"
                class="fixed inset-x-0 bottom-0 z-[710] max-h-[85dvh] overflow-hidden rounded-t-3xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700 dark:bg-zinc-900"
                @click.stop
            >
                <form
                    class="flex max-h-[85dvh] flex-col px-5 pb-6 pt-3"
                    novalidate
                    autocomplete="off"
                    @submit.prevent.stop="handleConfirm"
                    @keydown.enter.prevent.stop="handleConfirm"
                >
                    <div class="mb-3 flex justify-center">
                        <span class="h-1 w-10 rounded-full bg-zinc-300 dark:bg-zinc-600" />
                    </div>

                    <div class="mb-4 flex items-start justify-between gap-3">
                        <div>
                            <h3 class="text-base font-semibold text-zinc-900 dark:text-white">
                                {{ __('Crop image') }}
                            </h3>
                            <p class="mt-1 text-xs leading-5 text-zinc-500 dark:text-zinc-400">
                                {{ __('Adjust the frame, then add the cropped image.') }}
                            </p>
                        </div>

                        <button
                            type="button"
                            class="inline-flex size-9 shrink-0 items-center justify-center rounded-full text-zinc-500 transition hover:bg-zinc-100 dark:hover:bg-zinc-800"
                            :disabled="isProcessing"
                            @click.prevent.stop="handleClose"
                        >
                            <span class="text-lg leading-none">&times;</span>
                        </button>
                    </div>

                    <div class="crop-modal-stage mb-4 h-[min(42dvh,calc(85dvh-14rem))] overflow-hidden rounded-2xl bg-zinc-100 dark:bg-zinc-950">
                        <img
                            v-if="cropSource"
                            ref="cropImageMobile"
                            :src="cropSource"
                            alt=""
                            class="block size-full max-h-full max-w-full object-contain"
                        >
                    </div>

                    <div class="mt-auto space-y-3">
                        <button
                            type="button"
                            class="crop-modal-confirm-btn inline-flex w-full items-center justify-center rounded-2xl py-3.5 text-sm font-semibold text-white transition active:scale-[0.98] disabled:opacity-60"
                            :disabled="isProcessing"
                            @click.prevent.stop="handleConfirm"
                        >
                            {{ confirmLabel }}
                        </button>

                        <button
                            type="button"
                            class="w-full py-1 text-sm font-medium text-zinc-500 transition hover:text-zinc-700 disabled:opacity-60 dark:text-zinc-400 dark:hover:text-zinc-200"
                            :disabled="isProcessing"
                            @click.prevent.stop="handleClose"
                        >
                            {{ __('Cancel') }}
                        </button>
                    </div>
                </form>
            </div>
        </Transition>
    </Teleport>
</template>

<script>
export default {
    props: {
        open: {
            type: Boolean,
            default: false,
        },
        cropSource: {
            type: String,
            default: null,
        },
        isProcessing: {
            type: Boolean,
            default: false,
        },
        confirmLabel: {
            type: String,
            required: true,
        },
    },

    emits: ['close', 'confirm'],

    data() {
        return {
            isMobileLayout: false,
            mobileMediaQuery: null,
        }
    },

    watch: {
        /**
         * Locks page scroll while the desktop crop modal is open.
         *
         * @param {boolean} value
         */
        open(value) {
            if (!this.isMobileLayout) {
                document.body.style.overflow = value ? 'hidden' : ''
            }

            if (value) {
                document.addEventListener('keydown', this.handleDocumentKeydown, true)
            } else {
                document.removeEventListener('keydown', this.handleDocumentKeydown, true)
            }
        },
    },

    mounted() {
        this.mobileMediaQuery = window.matchMedia('(max-width: 767px)')
        this.isMobileLayout = this.mobileMediaQuery.matches
        this.mobileMediaQuery.addEventListener('change', this.handleMobileLayoutChange)
        Nova.$on('dismiss-field-modals', this.handleDismissRequest)
    },

    beforeUnmount() {
        this.mobileMediaQuery?.removeEventListener('change', this.handleMobileLayoutChange)
        document.removeEventListener('keydown', this.handleDocumentKeydown, true)
        document.body.style.overflow = ''
        Nova.$off('dismiss-field-modals', this.handleDismissRequest)
    },

    methods: {
        /**
         * Updates the mobile layout flag when the viewport changes.
         *
         * @param {MediaQueryListEvent} event
         */
        handleMobileLayoutChange(event) {
            this.isMobileLayout = event.matches
        },

        /**
         * Prevents Enter from submitting the parent Nova form while the modal is open.
         *
         * @param {KeyboardEvent} event
         */
        handleDocumentKeydown(event) {
            if (!this.open || this.isProcessing || event.key !== 'Enter') {
                return
            }

            event.preventDefault()
            event.stopPropagation()
            this.handleConfirm(event)
        },

        /**
         * Confirms the crop without submitting the parent Nova form.
         *
         * @param {Event} [event]
         */
        handleConfirm(event) {
            event?.preventDefault?.()
            event?.stopPropagation?.()

            if (this.isProcessing) {
                return
            }

            this.$emit('confirm')
        },

        /**
         * Closes the crop modal without submitting the parent Nova form.
         *
         * @param {Event} [event]
         */
        handleClose(event) {
            event?.preventDefault?.()
            event?.stopPropagation?.()

            if (this.isProcessing) {
                return
            }

            this.$emit('close')
        },

        /**
         * Closes the crop modal when a higher-priority system dialog opens.
         */
        handleDismissRequest() {
            if (!this.open || this.isProcessing) {
                return
            }

            this.$emit('close')
        },

        /**
         * Closes the modal when the blurred backdrop is clicked.
         */
        handleBackdropClick() {
            if (!this.isProcessing) {
                this.$emit('close')
            }
        },

        /**
         * Returns the active crop image element for Cropper.js.
         *
         * @returns {HTMLImageElement|null}
         */
        getCropImageElement() {
            if (this.isMobileLayout) {
                return this.$refs.cropImageMobile || null
            }

            return this.$refs.cropImage || null
        },
    },
}
</script>

<style scoped>
.crop-modal-fade-enter-active,
.crop-modal-fade-leave-active {
    transition: opacity 0.3s ease;
}

.crop-modal-fade-enter-from,
.crop-modal-fade-leave-to {
    opacity: 0;
}

.crop-modal-panel-enter-active {
    transition: all 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.crop-modal-panel-leave-active {
    transition: all 0.2s ease-in;
}

.crop-modal-panel-enter-from,
.crop-modal-panel-leave-to {
    opacity: 0;
    transform: translate(-50%, -50%) scale(0.95);
}

.crop-sheet-enter-active {
    transition: transform 0.3s cubic-bezier(0.22, 1, 0.36, 1);
}

.crop-sheet-leave-active {
    transition: transform 0.25s ease-in;
}

.crop-sheet-enter-from,
.crop-sheet-leave-to {
    transform: translateY(100%);
}

:deep(.cropper-view-box),
:deep(.cropper-face) {
    border-radius: 0.75rem;
}

:deep(.cropper-container) {
    max-height: 100%;
}

.crop-modal-confirm-btn {
    background-color: #2563eb;
}

.crop-modal-confirm-btn:hover:not(:disabled) {
    background-color: #1d4ed8;
}
</style>
